/**
 * The preview pane (decisions 9, 11, 12, 61; part 15): the pages around the cursor as the layout worker drew them, the
 * device profile picker with what the profile's pages stand for (exact for the PDF, approximate for an e-reader), and
 * each page's number (an estimate until the chapter has settled, decision 10). The pane follows the open chapter and
 * the layout (table Y): it asks the worker for nothing while it isn't shown.
 *
 * On the facing PDF profile the pane is a book (decision 70; part 15a): a spread, or one page on a narrow pane, turned
 * by the bar's buttons, a click or tap on the pages, or the wheel (tables TP, WH, BA).
 */
import { createEffect, createMemo, createSignal, on, onCleanup } from 'solid-js';
import type { Accessor } from 'solid-js';
import { profileById } from '@cockatrice/layout';
import type { BookSlot, PreviewSettings, ProfileId } from '@cockatrice/layout';
import type { Messages } from '../i18n/en.ts';
import { shownUnit } from '../preview/controller.ts';
import type { PreviewBook, PreviewDocument, PreviewState, ShownPage, ShownUnit } from '../preview/controller.ts';
import { bookScale, pageScale } from '../preview/scale.ts';
import type { PageScale } from '../preview/scale.ts';
import { swipeTo } from '../preview/swipe.ts';
import { WheelTurns } from '../preview/wheel.ts';
import type { NarrowPane, Panes } from '../shell/layout.ts';
import { PREVIEW_PROFILES } from '../shell/settings.ts';
import { cleanTitle } from '../shell/workspace.ts';
import { focusOn } from './announce.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import { component } from '../ui/solid.ts';
import { each, el, when } from '../ui/dom.ts';
import type { Child, On } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** Each profile's name in the picker. */
export const PROFILE_NAMES: Readonly<Record<ProfileId, keyof Messages & `preview.profile.${string}`>> = Object.freeze({
  'generic-6in': 'preview.profile.generic-6in',
  kindle: 'preview.profile.kindle',
  phone: 'preview.profile.phone',
  tablet: 'preview.profile.tablet',
  'pdf-a5': 'preview.profile.pdf-a5',
});

/** Part 11f: each profile's name on the pane's switch, short enough for five side by side. */
const SHORT_NAMES: Readonly<Record<ProfileId, keyof Messages & `preview.short.${string}`>> = Object.freeze({
  'generic-6in': 'preview.short.generic-6in',
  kindle: 'preview.short.kindle',
  phone: 'preview.short.phone',
  tablet: 'preview.short.tablet',
  'pdf-a5': 'preview.short.pdf-a5',
});

/** The pane's first control: its page size chosen. */
export const PROFILE_CHOSEN = '.preview-bar [role="radio"][aria-checked="true"]';

/**
 * The settings the open chapter is laid out with (part 14's page settings): the book's theme and language, the
 * profile picked on this device, and the chapter's title (cleaned as a rename cleans it) and place among the book's
 * chapters; null with no chapter open.
 */
export function previewSettings(shell: Shell): PreviewSettings | null {
  const { workspace } = shell;
  const book = workspace.book();
  const chapters = workspace.listing()?.chapters ?? [];
  const index = chapters.findIndex((entry) => entry.id === workspace.chapterId());
  const entry = chapters[index];
  if (book === null || entry === undefined) return null;
  return {
    book: book.id,
    theme: book.theme,
    profile: workspace.settings().previewProfile,
    language: book.metadata?.language ?? 'en-US',
    // Any string is stored as a title (a split takes a heading's text whole); the preview lays out what a rename would
    // store, so a long or untidy one can't fail the worker's check (V2) (deep-fix r2).
    title: cleanTitle(entry.title),
    number: index + 1,
  };
}

/**
 * M24 (part 15a): the open book as the book view reads it: its live chapters in order, each with the settings it is laid
 * out with (as {@link previewSettings} gives the open one), and the store's reader of a chapter's text; null with none.
 */
export function previewBook(shell: Shell): PreviewBook | null {
  const { workspace } = shell;
  const book = workspace.book();
  const chapters = workspace.listing()?.chapters;
  if (book === null || chapters === undefined) return null;
  const profile = workspace.settings().previewProfile;
  const language = book.metadata?.language ?? 'en-US';
  return {
    chapters: chapters.map((entry, index) => ({ id: entry.id, settings: { book: book.id, theme: book.theme, profile, language, title: cleanTitle(entry.title), number: index + 1 } })),
    reading: workspace.bookReading(),
    read: (chapterId) => workspace.chapterText(chapterId),
  };
}

/** The document the preview follows: the open editor with its chapter's settings, or null. */
export function previewDocument(shell: Shell): PreviewDocument | null {
  const editor = shell.workspace.editor();
  const settings = previewSettings(shell);
  if (editor === null || settings === null) return null;
  return { editor, settings, text: () => editor.parsed.text, cursor: () => editor.cursor };
}

/**
 * Y6–Y10, M1: keeps the controller told what to show and whether it is shown (the pane, the workspace's screen, the
 * page's own visibility). Runs while the workspace is in the page.
 */
export function followPreview(shell: Shell, shown: Accessor<Panes>): void {
  const { preview, workspace } = shell;
  createEffect(() => {
    preview.setVisible(shown().preview && workspace.screen() === 'workspace' && shell.pageVisible());
  });
  createEffect(() => {
    preview.setDocument(previewDocument(shell));
  });
  createEffect(() => {
    preview.setBook(previewBook(shell));
  });
  onCleanup(() => {
    preview.setVisible(false);
  });
}

/** A page's caption: its number, of how many once the chapter has settled, or "about" while it is an estimate. */
export function pageLabel(shell: Shell, page: ShownPage, total: number | null): string {
  const number = page.index + 1;
  if (!page.exact) return shell.t('preview.pageAbout', { number });
  return total === null ? shell.t('preview.page', { number }) : shell.t('preview.pageOf', { number, total });
}

/** BA1: a book view slot's caption: its chapter and page (about, while estimated), a blank page, a page being set, or nothing. */
export function slotLabel(shell: Shell, slot: BookSlot): string {
  switch (slot.kind) {
    case 'page':
      return shell.t(slot.exact ? 'preview.chapterPage' : 'preview.chapterPageAbout', { chapter: slot.chapter, number: slot.index + 1 });
    case 'blank':
      return shell.t('preview.blank');
    case 'pending':
      return shell.t('preview.pagePending');
    case 'none':
      return '';
  }
}

/** BA2, BA6: a unit's caption: its slots' captions, left then right, none of nothing. */
export function unitLabel(shell: Shell, unit: ShownUnit): string {
  const [left = '', right] = unit.slots.map((slot) => slotLabel(shell, slot)).filter((label) => label !== '');
  return right === undefined ? left : shell.t('preview.spread', { left, right });
}

/** BA2: the shown unit's caption, or nothing outside the book view. */
export function shownLabel(shell: Shell, state: PreviewState): string {
  const unit = shownUnit(state);
  return unit === null ? '' : unitLabel(shell, unit);
}

/** BA6: what a turn announces: the shown unit's caption while the book view has a place of its own, else nothing. */
export function turnNews(shell: Shell, state: PreviewState): string {
  return state.kind === 'book' && state.away ? shownLabel(shell, state) : '';
}

/** The pointer press a turn on the book's pages begins with (TP1, TP2): which pointer, and where. */
interface Press {
  readonly pointer: number;
  readonly x: number;
  readonly y: number;
}

/** TP2: how far a press may move and still turn, in CSS pixels. */
export const TURN_SLOP = 10;

/**
 * TP1–TP3, WH1–WH4: the handlers that turn the book over its pages: a press of the primary button or a touch, let go
 * where it began, right of the pages' middle forward, left of it back; a wheel gesture once. Pointer events, not
 * `click`, since a touch's click is the browser's to make (WebKit's emulated taps make none); a press the browser takes
 * for a scroll or a swipe is cancelled and never let go. A wheel with Ctrl or Meta is left to the browser (zoom).
 */
export function turnHandlers(shell: Shell): Record<string, unknown> {
  const wheel = new WheelTurns();
  let press: Press | null = null;
  const plain = (event: PointerEvent): boolean => event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
  return {
    onPointerDown: (event: On<PointerEvent, HTMLElement>) => {
      press = plain(event) ? { pointer: event.pointerId, x: event.clientX, y: event.clientY } : null;
    },
    onPointerCancel: () => {
      press = null;
    },
    onPointerUp: (event: On<PointerEvent, HTMLElement>) => {
      const begun = press;
      press = null;
      if (begun?.pointer !== event.pointerId || !plain(event)) return;
      if (Math.hypot(event.clientX - begun.x, event.clientY - begun.y) >= TURN_SLOP) return;
      const box = event.currentTarget.getBoundingClientRect();
      shell.preview.turn(event.clientX >= box.left + box.width / 2 ? 1 : -1);
    },
    onWheel: (event: On<WheelEvent, HTMLElement>) => {
      const input = { deltaX: event.deltaX, deltaY: event.deltaY, deltaMode: event.deltaMode, ctrlKey: event.ctrlKey, metaKey: event.metaKey, time: event.timeStamp };
      if (input.ctrlKey || input.metaKey) return;
      // The pages fit the pane, so a wheel over them turns rather than scrolls.
      event.preventDefault();
      const step = wheel.take(input);
      if (step !== 0) shell.preview.turn(step);
    },
  };
}

/** S1–S5: the handlers that switch a narrow screen's pane with a swipe over `from`. */
export function swipeHandlers(shell: Shell, from: NarrowPane): Record<string, unknown> {
  let start: { readonly pointer: string; readonly x: number; readonly y: number; readonly time: number } | null = null;
  return {
    onPointerDown: (event: On<PointerEvent, HTMLElement>) => {
      start = { pointer: event.pointerType, x: event.clientX, y: event.clientY, time: event.timeStamp };
    },
    onPointerCancel: () => {
      start = null;
    },
    onPointerUp: (event: On<PointerEvent, HTMLElement>) => {
      const begun = start;
      start = null;
      if (begun === null || shell.mode() !== 'narrow') return;
      const box = event.currentTarget.getBoundingClientRect();
      const to = swipeTo(from, {
        pointer: begun.pointer,
        startX: begun.x - box.left,
        startY: begun.y - box.top,
        endX: event.clientX - box.left,
        endY: event.clientY - box.top,
        width: box.width,
        startTime: begun.time,
        endTime: event.timeStamp,
      });
      if (to !== null) shell.ui.setNarrowPane(to);
    },
  };
}

/**
 * One page: its canvas, given the worker's pixels once, at the pane's size, its caption, and the text it shows for
 * assistive technology (part 11c, A1: the canvas is a picture of that text).
 */
function PageView(shell: Shell, page: ShownPage, size: Accessor<PageScale | null>, total: Accessor<number | null>, current: Accessor<boolean>): Child {
  return el(
    'figure',
    {
      class: () => `preview-page${current() ? ' current' : ''}`,
      'aria-current': () => (current() ? 'page' : undefined),
      // The cursor's page is scrolled into view when the cursor moves onto it.
      ref: (figure: HTMLElement) => {
        createEffect(
          on(current, (now) => {
            if (now) figure.scrollIntoView({ block: 'nearest' });
          }),
        );
      },
    },
    el('canvas', {
      'aria-hidden': 'true',
      ref: (canvas: HTMLCanvasElement) => {
        if (page.bitmap !== null) shell.present(canvas, page.bitmap);
      },
      style: () => {
        const scale = size();
        return scale === null ? {} : { width: `${scale.width}px`, height: `${scale.height}px` };
      },
    }),
    el('figcaption', {}, () => pageLabel(shell, page, total())),
    page.text === null ? null : el('div', { class: 'visually-hidden page-text' }, page.text),
  );
}

/**
 * One side of a look in the book view (BA1): a page's canvas, given the worker's pixels once, with its caption and text;
 * a blank page or a page being set as plain paper with its caption; nothing as the space a page would take. Hidden while
 * it isn't in the shown unit, so a turn within the window shows it at once (principle 5).
 */
function BookSlotView(shell: Shell, slot: BookSlot, size: Accessor<PageScale | null>, shown: Accessor<boolean>): Child {
  const box = (): Record<string, string> => {
    const scale = size();
    return scale === null ? {} : { width: `${scale.width}px`, height: `${scale.height}px` };
  };
  const caption = slotLabel(shell, slot);
  switch (slot.kind) {
    case 'page':
      return el(
        'figure',
        { class: 'preview-page', hidden: () => !shown(), 'data-slot': 'page' },
        el('canvas', {
          'aria-hidden': 'true',
          ref: (canvas: HTMLCanvasElement) => {
            if (slot.bitmap !== null) shell.present(canvas, slot.bitmap);
          },
          style: box,
        }),
        el('figcaption', {}, caption),
        slot.text === null ? null : el('div', { class: 'visually-hidden page-text' }, slot.text),
      );
    case 'blank':
    case 'pending':
      return el('figure', { class: `preview-page ${slot.kind}`, hidden: () => !shown(), 'data-slot': slot.kind }, el('div', { class: 'preview-paper', style: box }), el('figcaption', {}, caption));
    case 'none':
      return el('div', { class: 'preview-page none', hidden: () => !shown(), 'data-slot': 'none', 'aria-hidden': 'true' }, el('div', { class: 'preview-space', style: box }));
  }
}

/** BA3–BA5, BA7, BA13: the book view's turns in the bar: Previous, Next, and Back to the cursor while it has a place of its own. */
function TurnButtons(shell: Shell, single: Accessor<boolean>): Child {
  const { t, preview } = shell;
  const book = createMemo(() => {
    const now = preview.state();
    return now.kind === 'book' ? now : null;
  });
  // BA5: a button that can't turn stays focusable, says so, and does nothing.
  const button = (step: 1 | -1): Child =>
    el(
      'button',
      {
        type: 'button',
        class: step === 1 ? 'preview-next' : 'preview-previous',
        'aria-label': () => (step === 1 ? t(single() ? 'preview.nextPage' : 'preview.nextPages') : t(single() ? 'preview.previousPage' : 'preview.previousPages')),
        'aria-disabled': () => {
          const now = book();
          return now === null || (step === 1 ? now.end : now.start) ? 'true' : undefined;
        },
        onClick: () => {
          preview.turn(step);
        },
      },
      step === 1 ? t('preview.next') : t('preview.previous'),
    );
  return el(
    'div',
    { class: 'preview-turns', role: 'group', 'aria-label': t('preview.turns') },
    button(-1),
    button(1),
    when(
      () => book()?.away === true,
      () =>
        el(
          'button',
          {
            type: 'button',
            class: 'preview-back',
            onClick: () => {
              preview.backToCursor();
              // BA7: the button goes once the pages are back at the cursor; Next takes the focus first.
              focusOn(shell, '.preview-next');
            },
          },
          t('preview.backToCursor'),
        ),
    ),
  );
}

/**
 * The pane: the profile picker and its note, and the pages or what stands in for them; inert while `covered` (Y3). While
 * it is `alone` (a narrow screen showing it in the text's place, Y9) it is the page's main content (part 11c: a page has
 * one main landmark, and the text's is hidden then).
 */
export function PreviewPane(shell: Shell, covered: Accessor<boolean>, alone: Accessor<boolean>): Child {
  const { t, workspace, preview } = shell;
  const profile = createMemo(() => profileById(workspace.settings().previewProfile));
  const [width, setWidth] = createSignal(0);
  const [height, setHeight] = createSignal(0);
  // Z7: the bar's height. The bar is made before the pane that holds it, so its ref sets this before the pane's watch reads it.
  let barHeight: () => number;
  // Z6–Z8: the book view on the facing profile, fitting the pane under the bar; part 15's pages otherwise.
  const book = createMemo(() => (profile().facing ? bookScale(profile(), width(), height(), shell.pixelRatio()) : null));
  const size = createMemo(() => (profile().facing ? book() : pageScale(profile(), width(), shell.pixelRatio())));
  createEffect(() => {
    preview.setScale(size()?.scale ?? null);
  });
  createEffect(() => {
    const mode = book()?.mode;
    if (mode !== undefined) preview.setMode(mode);
  });
  const state = preview.state;
  // The book view's slots, every unit's in one list (kept by slot, so a held page keeps its canvas), and the shown unit.
  const slots = createMemo(() => {
    const now = state();
    return now.kind === 'book' ? now.units.flatMap((unit) => unit.slots) : [];
  });
  const shown = createMemo(() => shownUnit(state()));
  // What a shown window holds; nothing while the preview has no pages to show.
  const pages = createMemo(() => {
    const now = state();
    return now.kind === 'shown' ? now.pages : [];
  });
  const total = createMemo(() => {
    const now = state();
    return now.kind === 'shown' ? now.total : null;
  });
  const cursor = createMemo(() => {
    const now = state();
    return now.kind === 'shown' ? now.cursor : -1;
  });
  return el(
    // A section, which may take either role (an `aside` may not be the main content).
    'section',
    {
      class: 'preview-pane',
      'aria-label': t('preview.label'),
      role: () => (alone() ? 'main' : 'complementary'),
      inert: covered,
      ref: (element: HTMLElement) => {
        onCleanup(
          shell.watchSize(element, (paneWidth, paneHeight) => {
            setWidth(paneWidth);
            // Z7: the pages' room is the pane's height under the bar.
            setHeight(paneHeight - barHeight());
          }),
        );
      },
      ...swipeHandlers(shell, 'preview'),
    },
    el(
      'div',
      {
        class: 'preview-bar',
        ref: (element: HTMLElement) => {
          barHeight = () => element.offsetHeight;
        },
      },
      // Part 11f: the page size as the design's switch (the library's SegmentedControl), each size by its short name.
      component(SegmentedControl, {
        label: t('preview.profile'),
        class: 'preview-sizes',
        size: 'sm',
        options: PREVIEW_PROFILES.map((id) => ({ value: id, label: t(SHORT_NAMES[id]) })),
        get value() {
          return profile().id;
        },
        onChange: (value: string) => {
          for (const previewProfile of PREVIEW_PROFILES) if (previewProfile === value) void workspace.updateSettings({ previewProfile });
        },
      }),
      el('p', { class: 'preview-note' }, () => (profile().approximate ? t('preview.approximate') : t('preview.exact'))),
      when(
        () => profile().facing,
        () => TurnButtons(shell, () => book()?.mode === 'page'),
      ),
    ),
    () => {
      switch (state().kind) {
        case 'none':
          return el('p', { class: 'preview-status' }, t('preview.none'));
        case 'loading':
          return el('p', { class: 'preview-status', role: 'status' }, t('preview.loading'));
        case 'failed':
          return el(
            'div',
            { class: 'preview-status', role: 'alert' },
            el('p', {}, t('preview.failed')),
            el(
              'button',
              {
                type: 'button',
                onClick: () => {
                  preview.restart();
                  // Part 11c, A24: the button goes with the failure; the pane's first control takes the focus.
                  focusOn(shell, PROFILE_CHOSEN);
                },
              },
              t('preview.retry'),
            ),
          );
        case 'shown':
        case 'book':
          return null;
      }
    },
    when(
      () => state().kind === 'book',
      () =>
        el(
          'div',
          {
            class: 'preview-pages book',
            role: 'group',
            'aria-label': () => shownLabel(shell, state()),
            ...turnHandlers(shell),
          },
          each(slots, (slot) => BookSlotView(shell, slot, size, () => shown()?.slots.includes(slot) === true)),
        ),
      () =>
        el(
          'div',
          { class: 'preview-pages' },
          each(pages, (page) =>
            el(
              'div',
              { class: 'preview-slot', 'data-draw': String(page.draw) },
              PageView(shell, page, size, total, () => pages().indexOf(page) === cursor()),
            ),
          ),
        ),
    ),
  );
}
